import React from 'react';
import Button from 'src/components/common/Button';
import { Select, Input } from 'semantic-ui-react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import membersForms from 'src/services/api/membersForms';
import NewMemberModal from 'src/containers/experience/newMember/newMemberTutorForm';
import ReportButton from 'src/containers/admin/ReportButton';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { renderMonthElement } from 'src/components/form/Form';
import { getNewMembersColumnDefs } from 'src/components/common/rows/NewMemberRow';
import AGTable from 'src/components/table/aggrid';
import styled from '@emotion/styled';
import {
  AdminTableNetworkSection,
  AdminTableContent,
} from 'src/assets/styles/components/AdminTable';
import { crecerClase3ResultsStyles } from 'src/assets/styles/components/clase3Answers';
import { CONTACT_OPTIONS } from 'src/containers/conexion/constants';
import { chPerm, searchSelectProps } from 'src/components/helpers';

const CrecerNetworkAdminTable = styled(AdminTableNetworkSection)`
  ${crecerClase3ResultsStyles}
`;

const filters = [
  { key: 'Si', value: true, text: 'Si' },
  { key: 'No', value: false, text: 'No' },
];

const contactChannelFilters = CONTACT_OPTIONS.map((option) => ({
  key: option.value,
  value: option.value,
  text: option.label,
}));

const PER_PAGE = 50;

const NewMembersReport = (props) => {
  const [loaded, set_loaded] = React.useState(true);
  const [showNewMembers, set_showNewMembers] = React.useState(null);
  const [selectedMember, set_selectedMember] = React.useState(null);
  const [totalRecords, set_totalRecords] = React.useState(0);
  const [inputs, setInputs] = React.useState({ contacted: false });
  const [handleDate, set_handleDate] = React.useState({});
  const [focused, set_focused] = React.useState({});

  const gridApiRef = React.useRef(null);
  const filtersRef = React.useRef({ contacted: false });

  const inputsRef = React.useRef(inputs);
  inputsRef.current = inputs;

  const setFilters = (source = inputsRef.current) => {
    let filter = {};
    Object.entries(source)
      .filter((input) => input[1] != null)
      .forEach((element) => {
        filter[element[0]] = element[1];
      });
    return filter;
  };

  const makeDatasource = () => ({
    getRows: (params) => {
      set_loaded(false);
      const perPage = params.endRow - params.startRow || PER_PAGE;
      const currentPage = Math.floor(params.startRow / perPage) + 1;

      membersForms
        .newMembersReport({ filter: filtersRef.current, currentPage, perPage })
        .then((response) => {
          set_loaded(true);
          if (response.data.success) {
            const rows = response.data.newMember || [];
            const total =
              typeof response.data.total === 'number'
                ? response.data.total
                : params.startRow + rows.length;
            set_totalRecords(total);
            params.successCallback(rows, total);
          } else {
            set_totalRecords(0);
            params.successCallback([], 0);
          }
        })
        .catch((error) => {
          set_loaded(true);
          if (error.response?.status === 404) {
            set_totalRecords(0);
            params.successCallback([], 0);
          } else {
            if (error.response) Swal.fire(error.response.data.message);
            params.failCallback();
          }
        });
    },
  });

  const applyFilters = () => {
    filtersRef.current = setFilters();
    if (gridApiRef.current) {
      gridApiRef.current.setDatasource(makeDatasource());
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    filtersRef.current = setFilters();
    params.api.setDatasource(makeDatasource());
  };

  const onEnter = (e) => {
    if (e.key === 'Enter') applyFilters();
  };

  const selectHandle = (a, { value, name }) => {
    const nextInputs = { ...inputsRef.current, [name]: value };
    if (value == null) delete nextInputs[name];

    inputsRef.current = nextInputs;
    setInputs(nextInputs);
    applyFilters();
  };

  const handleInputs = (event) => {
    let name = event.target.name;
    let value = event.target.value;
    setInputs({ ...inputs, [name]: value });
  };

  const handleDateChange = (startDate, endDate) => {
    const rangeDate =
      startDate && endDate
        ? {
            startDate: startDate.clone().startOf('day').valueOf(),
            endDate: endDate.clone().endOf('day').valueOf(),
          }
        : null;

    const nextInputs = { ...inputsRef.current, rangeDate };
    if (rangeDate == null) delete nextInputs.rangeDate;

    inputsRef.current = nextInputs;
    setInputs(nextInputs);
    set_handleDate({ startDate, endDate });
  };

  const inputStyles = {
    display: 'inline-block',
    width: '45%',
    margin: ' 7px 0',
  };

  const onView = (member) => {
    set_selectedMember(member);
    set_showNewMembers(member.id);
  };

  const closeModal = () => {
    set_showNewMembers(null);
    set_selectedMember(null);
  };

  const changeNewMembersLine = (line) => {
    const api = gridApiRef.current;
    if (!api || !line) return;
    api.forEachNode((node) => {
      if (node?.data && parseInt(node.data.guest_id) === parseInt(line.guest_id)) {
        node.setData({ ...node.data, ...line });
      }
    });
  };

  const columnDefs = React.useMemo(() => getNewMembersColumnDefs(onView), []);

  return (
    <CrecerNetworkAdminTable>
      <AdminTableContent>
        <Button text={'Buscar'} onClick={applyFilters} />
        <div>
          <Input
            loading={!loaded}
            name={'keyword'}
            onChange={handleInputs}
            value={inputs.keyword || ''}
            className={'crecerp1c3'}
            style={inputStyles}
            onKeyPress={onEnter}
            placeholder="Buscar..."
          />
          <Select
            placeholder="Filtrar por contactado"
            style={inputStyles}
            loading={!loaded}
            {...searchSelectProps({
              name: 'contacted',
              value: inputs.contacted ?? null,
              handler: selectHandle,
              options: [{ key: null, value: null, text: 'Todos' }, ...filters],
            })}
          />
          <Select
            placeholder="Filtrar por canal de contacto"
            style={inputStyles}
            loading={!loaded}
            {...searchSelectProps({
              name: 'preferredContact',
              value: inputs.preferredContact ?? null,
              handler: selectHandle,
              options: [{ key: null, value: null, text: 'Todos' }, ...contactChannelFilters],
            })}
          />
          <DateRangePicker
            startDatePlaceholderText="Fecha Inicio"
            endDatePlaceholderText="Fecha Fin"
            startDate={handleDate.startDate}
            startDateId="startDateRange"
            minimumNights={0}
            onClose={() => applyFilters()}
            endDate={handleDate.endDate}
            endDateId="endDateRange"
            onDatesChange={({ startDate, endDate }) => handleDateChange(startDate, endDate)}
            displayFormat="DD/MM/YYYY"
            showClearDates
            focusedInput={focused}
            onFocusChange={(focused) => set_focused(focused)}
            isOutsideRange={() => false}
            renderMonthElement={renderMonthElement}
          />

          {chPerm(['newMembersAdmin']) && (
            <ReportButton
              URL={`/members/members-report`}
              fileName={`reporte_nuevos_miembros.xlsx`}
              event="Reporte Conexión"
              filters={{ filter: setFilters() }}
            />
          )}
        </div>
      </AdminTableContent>
      <div>
        <p>Total de registros: {totalRecords}</p>
        <AGTable
          columnDefs={columnDefs}
          rowModelType="infinite"
          onGridReady={onGridReady}
          pagination
          paginationPageSize={PER_PAGE}
          cacheBlockSize={PER_PAGE}
          rowHeight={60}
          suppressDragLeaveHidesColumns={false}
          defaultCol={{ sortable: false, filter: false, floatingFilter: false }}
        />
      </div>

      {showNewMembers && selectedMember && (
        <NewMemberModal
          set_showNewMembers={closeModal}
          showNewMembers={showNewMembers}
          changeNewMembersLine={changeNewMembersLine}
          newMember={selectedMember}
        />
      )}
    </CrecerNetworkAdminTable>
  );
};

export default NewMembersReport;
